import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { resendVerificationEmail } from '../services/api';
import { PrimaryButton, SecondaryButton } from './buttons';

/**
 * VerifyEmailPage - Shown when user is logged in but email is not verified
 * Displays a prompt to check email and allows resending verification email
 */
const VerifyEmailPage: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [resendMessage, setResendMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Check if user is already verified
  const checkVerificationStatus = async () => {
    setIsChecking(true);
    try {
      // Add a minimum 2-second delay for better UX
      const [result] = await Promise.all([
        refreshUser(),
        new Promise(resolve => setTimeout(resolve, 2000))
      ]);
      // After refresh, if user is verified, navigate to account setup or dashboard
      const updatedUser = await refreshUser();
      if (updatedUser && updatedUser.emailVerified) {
        navigate('/my-splits');
      }
    } catch (error) {
      // Silently fail - user will stay on this page
    } finally {
      setIsChecking(false);
    }
  };

  // Check on mount only
  useEffect(() => {
    checkVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendMessage(null);
    
    try {
      await resendVerificationEmail();
      setResendMessage({ 
        type: 'success', 
        text: 'Verification email sent! Check your inbox.' 
      });
    } catch (error) {
      setResendMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to resend verification email' 
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-secondary flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <img src="/fulllogo.png" alt="Cotune" className="h-10 w-auto mx-auto" />
          </Link>
        </div>
        
        <div className="bg-surface-secondary border border-border-subtle rounded-lg p-8 text-center">
          {/* Email Icon */}
          <div className="mx-auto w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mb-6">
          <svg 
            className="w-8 h-8 text-accent" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-content mb-2">
          Verify Your Email
        </h1>
        
        <p className="text-content-secondary mb-6">
          We've sent a verification email to:
        </p>
        
        <p className="text-content font-medium bg-accent/10 rounded-lg py-3 px-4 mb-6">
          {user?.email}
        </p>
        
        <p className="text-content-muted text-sm mb-8">
          Click the link in the email to verify your account and access CoTune.
          The link will expire in 24 hours.
        </p>

        {/* Resend Message */}
        {resendMessage && (
          <div className={`mb-6 p-3 rounded-lg text-sm ${
            resendMessage.type === 'success' 
              ? 'bg-success-muted text-success border border-success/20' 
              : 'bg-error-muted text-state-error border border-state-error/20'
          }`}>
            {resendMessage.text}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <PrimaryButton
            onClick={checkVerificationStatus}
            disabled={isChecking}
            loading={isChecking}
            className="w-full"
          >
            {isChecking ? 'Checking...' : "I've Verified My Email"}
          </PrimaryButton>
          
          <SecondaryButton
            onClick={handleResendVerification}
            disabled={isResending}
            className="w-full"
          >
            {isResending ? 'Sending...' : 'Resend Verification Email'}
          </SecondaryButton>
          
          <button
            onClick={logout}
            className="w-full text-content-muted hover:text-content text-sm py-2"
          >
            Sign Out
          </button>
        </div>

        {/* Help Text */}
        <p className="mt-8 text-content-muted text-xs">
          Didn't receive the email? Check your spam folder or try resending.
        </p>
      </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
